import React from "react";

import {
  Typography,
  useMediaQuery,
  useTheme,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import { useForm, Controller } from "react-hook-form";

import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { reportViolation } from "@/utils/violationReporting";

interface ReportFormInputs {
  reason: string;
  description: string;
}

interface Props {
  setSuccess: (success: boolean) => void;
  reportedUserId: string;
  currentUserId: string;
  onClose: () => void;
}

const ReportForm: React.FC<Props> = ({ setSuccess, reportedUserId, currentUserId, onClose }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up("sm"));

  const { authenticatedAxios } = useFirebaseContext();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ReportFormInputs>();

  const reportReasons = [
    "Inappropriate behavior",
    "Harassment or bullying",
    "Spam or unwanted messages",
    "False information",
    "Inappropriate content",
    "Other",
  ];

  const onSubmit = async (data: ReportFormInputs) => {
    try {
      await reportViolation(
        authenticatedAxios,
        currentUserId,
        reportedUserId,
        data.reason,
        data.description
      );
      setSuccess(true);
    } catch {
      setError("root", {
        message: "Something went wrong, please try again later.",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <Box sx={{
 display: "flex",
flexDirection: "column",
gap: 2 
}}>
          <Controller
            name="reason"
            control={control}
            defaultValue=""
            rules={{ required: "Please select a reason for reporting" }}
            render={({ field, fieldState: {error} }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel>Reason for Report</InputLabel>
                <Select {...field} label="Reason for Report">
                  {reportReasons.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <Typography variant="caption" color="error">
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="description"
            control={control}
            defaultValue=""
            rules={{ required: "Please provide a description" }}
            render={({ field, fieldState: {error} }) => (
              <TextField
                {...field}
                label="Description"
                multiline
                rows={4}
                fullWidth
                error={!!error}
                helperText={error?.message || "Please provide details about the issue"}
                placeholder="Please describe what happened and why you're reporting this user..."
              />
            )}
          />
        </Box>
        {errors.root && (
          <Typography color="error" sx={{ mt: 2 }}>
            {errors.root.message}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: isSm ? 3 : 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          color="secondary"
        >
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="error">
          Submit Report
        </Button>
      </DialogActions>
    </form>
  );
};

export default ReportForm; 